import { Injectable } from '@angular/core';
import { DateService } from './date.service';

export interface FixDateConverterOptions {
  preferredFrom?: Date;
  preferredTo?: Date;
  unknownReplacement?: string;
  century?: number;
}

export interface Breakdown {
  day: number | undefined;
  month: number | undefined;
  year: number;
}

type Decoder = (text: string) => Breakdown[];

// Erstellt verschiedene Decoder zur Interpretation von Datumsformaten
function makeDecoders(century: number): { [key: string]: Decoder } {
  // Generische Funktion zur Erstellung von Decodern
  function createDecoder(
    regex: RegExp,
    splitter: (text: string) => string[],
    positions: [number, number, number],
    century: number
  ): Decoder {
    return (text: string): Breakdown[] => {
      const res: Breakdown[] = [];
      if (regex.test(text)) {
        const arr = splitter(text);
        if (arr.length !== 3) return res;

        const day = parseInt(arr[positions[0]], 10) || undefined;
        const month = parseInt(arr[positions[1]], 10) || undefined;
        const yearStr = arr[positions[2]];
        const year = parseInt(yearStr, 10);

        if (yearStr.length === 4) {
          res.push({ year, month, day });
        } else if (yearStr.length === 2) {
          const year = parseInt(yearStr, 10);
          if (!isNaN(year)) {
            // Aktuelles Jahrhundert wird zuerst hinzugefügt
            res.push(
              { year: century + year, month, day },        // Option: Aktuelles Jahrhundert
              { year: century - 100 + year, month, day },  // Option: Vorheriges Jahrhundert
              { year: century + 100 + year, month, day }   // Option: Nächstes Jahrhundert
            );
          }
        }
      }
      return res;
    };
  }

  // Standard-Splitter für Trennzeichen
  const defaultSplitter = (separator: string) => (text: string) =>
    text.split(separator);

  // Spezieller Splitter für Eingaben ohne Trennzeichen
  const onlyDigitsSplitter = (text: string): string[] => {
    const len = text.length;
    if (len === 6 || len === 8) {
      return [text.slice(0, 2), text.slice(2, 4), text.slice(4)];
    }
    return [];
  };

  return {
    onlyDigits: createDecoder(
      /^(?:\d{6}|\d{8})$/,
      onlyDigitsSplitter,
      [0, 1, 2],
      century
    ),
    germanDots: createDecoder(
      /^\d{1,2}\.\d{1,2}\.\d{2,4}$/,
      defaultSplitter('.'),
      [0, 1, 2],
      century
    ),
    commas: createDecoder(
      /^\d{1,2},\d{1,2},\d{2,4}$/,
      defaultSplitter(','),
      [0, 1, 2],
      century
    ),
    englishSlashes: createDecoder(
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
      defaultSplitter('/'),
      [0, 1, 2],
      century
    ),
    iso: createDecoder(
      /^\d{1,2}-\d{1,2}-\d{2,4}$/,
      defaultSplitter('-'),
      [0, 1, 2],
      century
    ),
  };
}

@Injectable()
export class FixDateService {
  private readonly century: number;

  constructor(private dateService: DateService) {
    const year = this.dateService.now().getFullYear();
    this.century = year - (year % 100); // Aktuelles Jahrhundert
  }

  getConverter(options: FixDateConverterOptions = {}): FixDateConverter {
    if (options.century === undefined) {
      options.century = this.century; // Sicherstellen, dass century gesetzt wird
    }
    return new FixDateConverter(options);
  }
}

export class FixDateConverter {
  private readonly decoders: Decoder[];
  private readonly preferredFrom: Date;
  private readonly preferredTo: Date;
  private readonly unknownReplacement: string;
  private cache: {
    [key: string]: { formatted: string; breakdown: Breakdown };
  } = {};
  private lastValue: string;

  constructor(options: FixDateConverterOptions = {}) {
    this.preferredFrom = options.preferredFrom;
    this.preferredTo = options.preferredTo;
    this.unknownReplacement = options.unknownReplacement || '';

    // Fallback: Wenn kein Jahrhundert angegeben ist, verwenden wir das aktuelle Jahrhundert
    if (options.century === undefined) {
      const currentYear = new Date().getFullYear();
      options.century = currentYear - (currentYear % 100);
    }

    const builtinDecoders = makeDecoders(options.century);
    this.decoders = [
      builtinDecoders.onlyDigits,
      builtinDecoders.germanDots,
      builtinDecoders.commas,
      builtinDecoders.englishSlashes,
      builtinDecoders.iso,
    ];
  }

  // Konvertiert einen Eingabestring in ein formatiertes Datum
  convert(value: string): string {
    let formatted = value;
    if ((value && value.length === 8) || value && value.length >= (this.lastValue ? this.lastValue.length : 0)) {
      const cached = this.cache[value];
      if (!cached) {
        const breakdowns = this.decode(value);
        const valids = this.check(breakdowns);
        const best = this.select(valids);
        formatted = best ? this.encode(best) : value;
        if (formatted) {
          this.cache[value] = { formatted, breakdown: best };
        }
      } else {
        formatted = cached.formatted;
      }
    }
    this.lastValue = value;
    return formatted;
  }

  // Konvertiert einen Eingabestring in ein Breakdown-Objekt
  convertToBreakdown(value: string): Breakdown {
    let breakdown: Breakdown;
    if (value && value.length >= (this.lastValue ? this.lastValue.length : 0)) {
      const cached = this.cache[value];
      if (!cached) {
        const breakdowns = this.decode(value);
        const valids = this.check(breakdowns);
        breakdown = this.select(valids);
        const formatted = breakdown ? this.encode(breakdown) : value;
        if (formatted) {
          this.cache[value] = { formatted, breakdown };
        }
      } else {
        breakdown = cached.breakdown;
      }
    }
    this.lastValue = value;
    return breakdown;
  }

  // Zerlegt den Eingabestring in mögliche Datumskomponenten
  private decode(text: string): Breakdown[] {
    const res: Breakdown[] = [];
    if (text) {
      text = text.trim();
      for (const decoder of this.decoders) {
        res.push(...decoder(text));
      }
    }
    return res;
  }

  // Überprüft die möglichen Datumsangaben auf Gültigkeit
  private check(list: Breakdown[]): Breakdown[] {
    const res: Breakdown[] = [];
    for (const breakdown of list) {
      const day = breakdown.day ?? 1;
      const month = (breakdown.month ?? 1) - 1; // Monate in JS von 0-11
      const date = new Date(breakdown.year, month, day);
      if (
        date.getFullYear() === breakdown.year &&
        date.getMonth() === month &&
        date.getDate() === day
      ) {
        res.push(breakdown);
      }
    }
    return res;
  }

  // Wählt das passendste Datum aus
  private select(list: Breakdown[]): Breakdown | undefined {
    let best: Breakdown | undefined;
    let fallback: Breakdown | undefined;
    let latestBeforePreferred: { date: Date; breakdown: Breakdown } | undefined;
    let earliestAfterPreferred: { date: Date; breakdown: Breakdown } | undefined;

    for (const breakdown of list) {
      const day = breakdown.day ?? 1;
      const month = (breakdown.month ?? 1) - 1;
      const date = new Date(breakdown.year, month, day);

      if (this.preferredFrom && this.preferredTo) {
        if (date >= this.preferredFrom && date <= this.preferredTo) {
          // Bevorzuge Datumsangaben innerhalb des gewünschten Bereichs
          if (!best || date < new Date(best.year, (best.month ?? 1) -1, best.day ?? 1)) {
            best = breakdown;
          }
        } else if (date < this.preferredFrom) {
          // Datum liegt vor dem bevorzugten Bereich
          if (!latestBeforePreferred || date > latestBeforePreferred.date) {
            latestBeforePreferred = { date, breakdown };
          }
        } else if (date > this.preferredTo) {
          // Datum liegt nach dem bevorzugten Bereich
          if (!earliestAfterPreferred || date < earliestAfterPreferred.date) {
            earliestAfterPreferred = { date, breakdown };
          }
        }
      } else {
        // Kein bevorzugter Bereich angegeben, setze Fallback
        if (!fallback) {
          fallback = breakdown;
        }
      }
    }

    if (best) {
      return best;
    } else if (latestBeforePreferred) {
      return latestBeforePreferred.breakdown;
    } else if (earliestAfterPreferred) {
      return earliestAfterPreferred.breakdown;
    } else {
      return fallback;
    }
  }

  // Formatiert das Datum als String
  private encode(breakdown: Breakdown): string {
    const xx = this.unknownReplacement;
    return (
      format02d(breakdown.day) +
      '.' +
      format02d(breakdown.month) +
      '.' +
      breakdown.year
    );

    function format02d(nr: number | undefined): string {
      if (nr === undefined) return xx;
      return nr < 10 ? '0' + nr : nr.toString();
    }
  }
}
